import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import pLimit from "p-limit";

import { MAX_PARALLEL_DOCUMENT_UPLOADS, useVariableValue } from "lib/featureFlags";
import {
  CreateCustomerDocumentsMutation,
  CreateCustomerDocumentsRequest_NewDocuments,
} from "lib/generated/app-service-gql/graphql";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { CustomerDocumentStatus, CustomerDocumentType } from "lib/types/graphQLEnums";
import { UpdateFileStatus } from "../../../../components/content_library/DocumentUploadProgressProvider";
import { FileUploadStatus, FileWithDirectoryPath } from "./types";
import { useCreateCustomerDocuments } from "./useCreateCustomerDocuments";
import { useStartCustomerDocumentProcessing } from "./useStartCustomerDocumentProcessing";
import { useStoreCustomerDocument } from "./useStoreCustomerDocument";

type CustomerDocumentWithFile =
  CreateCustomerDocumentsMutation["createCustomerDocuments"]["documents"][number] & {
    file?: FileWithDirectoryPath;
  };

export type UploadFilesMutationInput = {
  files: FileWithDirectoryPath[];
  resourceId?: string;
  // The operation these files belong to in the upload-progress provider (a
  // bidId, or — when omitted — the current user's guid for content-library
  // uploads). Used to scope every `updateFileStatus` call so two in-flight
  // uploads sharing a filename on different operations can't cross over.
  operationId?: string;
  uiPath?: string;
  uploadStatuses: FileUploadStatus[];
  // When true, the document-processing kickoff (`startProcessingDocument`) is
  // skipped per file — the processing is happening in the backend instead
  // (e.g. in a bid orchestrator hatchet workflow).
  skipProcessing?: boolean;
};

/**
 * Handles the upload and creation of a customer document. Uploads the document
 * to an s3 bucket, handles the creation of the customer document in the
 * database, as well as initiating its processing (chunking & indexing)
 * @param param0
 * @returns
 */
export function useUploadCustomerDocuments(
  updateFileStatus: UpdateFileStatus,
  documentType: CustomerDocumentType,
) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const { mutateAsync: storeCustomerDocument } = useStoreCustomerDocument(updateFileStatus);

  // createDocuments matches files by fileName only and has no operationId, so it
  // can't safely scope status updates across concurrent same-named uploads. We
  // drive every status update from here instead, where the operationId is known.
  const { mutateAsync: createDocuments } = useCreateCustomerDocuments();
  const { mutateAsync: startProcessingDocument } =
    useStartCustomerDocumentProcessing(updateFileStatus);
  const maxParallelDocumentUploads = useVariableValue(MAX_PARALLEL_DOCUMENT_UPLOADS, 1);

  return useMutation<void, AxiosError, UploadFilesMutationInput>({
    mutationFn: async ({
      files,
      resourceId,
      operationId: inputOperationId,
      uiPath,
      uploadStatuses,
      skipProcessing,
    }) => {
      const limit = pLimit(maxParallelDocumentUploads);
      // Mirror how the provider keys operations: bidId for bid uploads, the
      // user's guid for content-library uploads.
      const operationId = inputOperationId ?? currentUser?.guid;

      const newDocuments: CreateCustomerDocumentsRequest_NewDocuments[] = [];

      for (const file of files) {
        let fullUiPath: string | undefined;
        if (documentType === CustomerDocumentType.ContentLibrary) {
          const directoryPath = file.directoryPath;

          // Full ui path is the path of the directory of the
          // uploaded file (if it exists), plus the directory if the
          // ui folder path it's been uploaded in (if it was
          // uploaded in one)
          const folderPath = [uiPath, directoryPath].filter(Boolean);
          fullUiPath = folderPath.length > 0 ? folderPath.join("/") : undefined;
        }
        const doc: CreateCustomerDocumentsRequest_NewDocuments = {
          fileName: file.name.trim(),
          isFolder: false,
          metadata: {
            fileSizeBytes: file.size,
            mimeType: file.type,
          },
          documentType,
          uiPath: fullUiPath,
          resourceId: resourceId,
        };
        newDocuments.push(doc);
      }

      let response;
      try {
        const result = await createDocuments({
          request: {
            newDocuments,
          },
        });
        response = result.createCustomerDocuments;
      } catch (error) {
        // If createDocuments fails, mark all files as errored so the UI can recover
        for (const file of files) {
          updateFileStatus({
            operationId,
            fileName: file.name,
            documentId: undefined,
            updates: { isError: true, isFinished: true, isProcessed: false },
          });
        }
        throw error;
      }

      void queryClient.invalidateQueries({ queryKey: ["customerDocuments"] });

      // Attach the documentId to every non-folder file (and settle duplicates the
      // backend already has as SUCCESS). Scoped by operationId so the fileName
      // match can't bleed into another operation's same-named file.
      for (const doc of response.documents) {
        if (doc.isFolder) continue;
        const isDuplicate = doc.status === CustomerDocumentStatus.Success;
        updateFileStatus({
          operationId,
          fileName: doc.fileName,
          documentId: doc.id,
          updates: {
            isError: false,
            isUploaded: isDuplicate,
            isProcessed: isDuplicate,
            isFinished: isDuplicate,
          },
        });
      }

      // Filter out documents that are already processed, are folders or do not
      // have a signed url
      const docs = response.documents.filter(
        (doc) => !doc.isFolder && doc.signedUrl && doc.status !== CustomerDocumentStatus.Success,
      );

      // Map the response documents to the FileWithDirectoryPath type -
      // assuming no duplicate file names
      const customerDocsWithFile: CustomerDocumentWithFile[] = docs.map((doc) => ({
        ...doc,
        file: files.find((f) => f.name === doc.fileName),
      }));
      const processFile = async (doc: CustomerDocumentWithFile) => {
        const currentFileStatus = uploadStatuses.find(
          (status) => status.file.name === doc.fileName,
        );
        if (currentFileStatus?.isUploaded && currentFileStatus.isProcessed) {
          return;
        }
        try {
          const { id, file, signedUrl } = doc;
          if (!signedUrl) {
            throw new Error("No signed URL provided for document upload");
          }
          if (!file) {
            throw new Error(`File not found for document: ${doc.fileName}`);
          }
          await storeCustomerDocument({ id, file, signedUrl, operationId });
          if (skipProcessing) {
            // Processing happens async in the backend (e.g. the BidOrchestrator hatchet
            // workflow), which publishes per-file status to the same
            // `customerDocumentProcessingStatus` subscription (keyed on the bid's
            // resourceId). The provider marks each file processed/finished as those
            // events arrive, so we only flag the S3 upload as done here. Leaving the
            // file unfinished keeps `filesProcessing` true, which keeps that
            // subscription enabled so the progress bar tracks processing to completion.
            updateFileStatus({
              operationId,
              fileName: doc.fileName,
              documentId: doc.id,
              updates: { isUploaded: true, isProcessed: false },
            });
          } else {
            await startProcessingDocument({ docId: doc.id });
          }
        } catch (error) {
          updateFileStatus({
            operationId,
            fileName: doc.fileName,
            documentId: doc.id,
            updates: { isError: true, isFinished: true, isProcessed: false },
          });
          throw error;
        }
      };
      await Promise.all(customerDocsWithFile.map((d) => limit(() => processFile(d))));
    },
  });
}
